import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { ENV_VARS } from "@/constants/env";
import { signLinkSession } from "./signLinkSession";
import { LANGUAGE_HEADER, requestLanguage } from "@/i18n/request-language";

export type SignMethod = "DRAWN" | "TYPED" | "UPLOADED";

export interface ExchangeTokenRequest {
  token: string;
}

export interface SignFieldRect {
  label: string;
  x: string;
  y: string;
  width: string;
  height: string;
}

export interface SignFieldRectWithPage extends SignFieldRect {
  pageIndex: number;
}

export interface SignFieldLayout {
  firstPage?: SignFieldRect;
  perPage?: SignFieldRect;
  lastPageOnly?: boolean;
  /**
   * Per-signature-block rect keyed by node id (authoritative). Each rect
   * carries its own `pageIndex` so multi-page docs with one sig on page 1
   * place the overlay correctly instead of on the last page.
   */
  bySigId?: Record<string, SignFieldRectWithPage>;
}

export interface SignLinkSignaturePayload {
  id: number;
  documentId: number;
  status: "unsigned" | "signed" | "rejected" | "pending";
  document: {
    id: number;
    name: string;
    type: string;
    fileUrl: string;
  };
  fieldLayout?: SignFieldLayout;
  repositionable?: boolean;
}

export interface ExchangeTokenResponse {
  sessionToken: string;
  user: {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
  };
  signatures: SignLinkSignaturePayload[];
  expiresAt: string;
}

export interface SignDocumentBody {
  signatureData: string;
  signMethod: SignMethod;
  consentAcceptedAt: string;
  consentText: string;
  metadata?: Record<string, unknown>;
}

export interface RejectDocumentBody {
  rejectionReason: string;
}

export interface ESigCertificate {
  name: string;
  email: string;
  position: string;
  ipAddress: string;
  department: string;
  timestamp: string;
  signatureImage: string | null;
  initialsImage: string | null;
  documentName?: string;
  documentType?: string;
  signedDocumentCount?: number;
  /**
   * Per-doc rows when this certificate covers more than one signature in
   * the same batch. Empty/undefined for the legacy single-doc cert path.
   */
  documents?: Array<{
    signatureId: number;
    documentId: number;
    documentName?: string;
    documentType?: string;
    timestamp?: string;
    ipAddress?: string;
  }>;
  companyName?: string;
  companyAddress?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyWebsite?: string;
}

const signLinkBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  return fetchBaseQuery({
    baseUrl: ENV_VARS.API_INSTANCE,
    prepareHeaders: (headers) => {
      // Sign-session JWT is held in memory only (signLinkSession). Skip the
      // user JWT path used elsewhere — those two auth contexts must not mix.
      // Note: do not add Cache-Control here — it's not in the BE CORS
      // allowedHeaders list and would trigger preflight rejection.
      const token = signLinkSession.get();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set(LANGUAGE_HEADER, requestLanguage());
      return headers;
    },
  })(args, api, extraOptions);
};

// BE wraps every response with { success, statusCode, message, data, timestamp }
// via a global ResponseInterceptor. Unwrap `.data` for every endpoint so
// callers see the typed payload directly.
const unwrap = <T,>(res: any): T =>
  (res && typeof res === "object" && "data" in res ? res.data : res) as T;

export const signLinkApi = createApi({
  reducerPath: "signLinkApi",
  baseQuery: signLinkBaseQuery,
  tagTypes: ["signSession"],
  endpoints: (builder) => ({
    exchangeToken: builder.mutation<ExchangeTokenResponse, ExchangeTokenRequest>({
      query: (body) => ({
        url: "/sign-link/exchange",
        method: "POST",
        body,
      }),
      transformResponse: (res) => unwrap<ExchangeTokenResponse>(res),
    }),
    finalizeSignSession: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: "/sign-link/finalize",
        method: "POST",
      }),
      transformResponse: (res) => unwrap<{ success: boolean }>(res),
    }),
    resendSignLink: builder.mutation<
      { expiresInSeconds: number },
      { userId: number }
    >({
      query: (body) => ({
        url: "/sign-link/resend",
        method: "POST",
        body,
      }),
      transformResponse: (res) => unwrap<{ expiresInSeconds: number }>(res),
    }),
    signDocument: builder.mutation<
      unknown,
      { sigId: number; body: SignDocumentBody }
    >({
      query: ({ sigId, body }) => ({
        url: `/sign-link/signatures/${sigId}/sign`,
        method: "POST",
        body: {
          ...body,
          metadata: body.metadata ? JSON.stringify(body.metadata) : undefined,
        },
      }),
      transformResponse: (res) => unwrap<unknown>(res),
    }),
    rejectDocument: builder.mutation<
      unknown,
      { sigId: number; body: RejectDocumentBody }
    >({
      query: ({ sigId, body }) => ({
        url: `/sign-link/signatures/${sigId}/reject`,
        method: "POST",
        body,
      }),
      transformResponse: (res) => unwrap<unknown>(res),
    }),
    getCertificate: builder.query<ESigCertificate, { sigId: number }>({
      query: ({ sigId }) => ({
        url: `/sign-link/signatures/${sigId}/certificate`,
        method: "GET",
      }),
      transformResponse: (res) => unwrap<ESigCertificate>(res),
    }),
  }),
});

export const {
  useExchangeTokenMutation,
  useFinalizeSignSessionMutation,
  useResendSignLinkMutation,
  useSignDocumentMutation,
  useRejectDocumentMutation,
  useGetCertificateQuery,
  useLazyGetCertificateQuery,
} = signLinkApi;
